import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import AuthModal from './AuthModal'
import { APP_ENV } from '../services/api'
import { initialNavState, nextNavState } from './headerScroll'
import './Header.css'

function Header() {
  const { user, signOut, isAuthModalOpen, openAuthModal, closeAuthModal } = useAuth()
  const { getTotalItems } = useCart()
  const location = useLocation()
  const [isHovered, setIsHovered] = useState(false)
  const hoverTimeoutRef = useRef(null)
  const userMenuRef = useRef(null)
  // 移动端：向下滚动时把分类那一行收起来（规则见 headerScroll.js）
  const [navCollapsed, setNavCollapsed] = useState(false)
  
  const isActive = (path) => {
    if (path === '/all-products') {
      return location.pathname === '/all-products' || location.pathname === '/products'
    }
    return location.pathname === path
  }

  // 处理鼠标进入
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setIsHovered(true)
  }

  // 处理鼠标离开
  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false)
    }, 150) // 150ms延迟
  }

  // 清理定时器
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  // 滚动方向决定分类行的收放（规则和为什么不能按单次位移判断，见 headerScroll.js）。
  // scroll 事件一秒能来上百次，所以只在 rAF 里取一次 scrollY，
  // 别每次事件都读——读它会触发布局。
  //
  // ⚠️ 必须先算完再 setState，不能写成 setNavCollapsed(c => f(c, prevY, y))：
  //    prevY 是会被改写的闭包变量，而函数式 updater 要等 React 渲染时才调用，
  //    那时 prevY 早被下面那行改成 y 了，位移永远算成 0，分类行永远不会收起。
  useEffect(() => {
    let state = initialNavState(window.scrollY)
    let ticking = false

    const onScroll = () => {
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(() => {
        state = nextNavState(state, window.scrollY, Date.now())
        ticking = false
        setNavCollapsed(state.collapsed)
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // 换页面时把分类行放回来。新页面如果不在顶部，滚动事件不会自己触发，
  // 分类会一直是收起状态。
  useEffect(() => {
    setNavCollapsed(false)
  }, [location.pathname])

  return (
    <header className={`header${navCollapsed ? ' nav-collapsed' : ''}`}>
      <div className="header-container">
        <div className="logo">
          <Link to="/">
            <h1>p1ur{APP_ENV !== 'production' && <span className="env-badge">{APP_ENV}</span>}</h1>
          </Link>
        </div>
        <nav className="nav">
          <ul>
            <li><Link to="/all-products" className={isActive('/all-products') ? 'active' : ''}>All Products</Link></li>
            <li><Link to="/vibrators" className={isActive('/vibrators') ? 'active' : ''}>Vibrators</Link></li>
            <li><Link to="/lingerie" className={isActive('/lingerie') ? 'active' : ''}>Lingerie</Link></li>
            <li><Link to="/about" className={isActive('/about') ? 'active' : ''}>About</Link></li>
          </ul>
        </nav>
        <div className="user-actions">
          {user ? (
            <div 
              className="user-menu"
              ref={userMenuRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Link to="/profile" className="account-btn" aria-label="Account">
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </Link>
              {isHovered && (
                <div className="user-dropdown">
                  <div className="user-info">
                    <p className="user-name">{user.name}</p>
                    <p className="user-email">{user.email}</p>
                  </div>
                  <button className="signout-btn" onClick={signOut}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="account-btn" onClick={() => openAuthModal('/')} aria-label="Login">
              <svg className="icon" viewBox="0 0 24 24">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>
          )}
          <Link to="/cart" className="cart-btn" aria-label="Cart">
            <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h8.5M17 18a1 1 0 100 2 1 1 0 000-2zM9 18a1 1 0 100 2 1 1 0 000-2z"/>
            </svg>
            {getTotalItems() > 0 && (
              <span className="cart-badge">{getTotalItems()}</span>
            )}
          </Link>
        </div>
      </div>
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
    </header>
  )
}

export default Header